'use client'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  getAvailableQuotes,
  UnifiedInstrumentKind,
} from '@/features/order/order-form/lib/instrument-filter'
import { Instrument } from '@/types/generated/common/types'

interface QuoteAssetDropdownProps {
  base: string
  quote: string
  kind?: UnifiedInstrumentKind
  onQuoteChange?: (newQuote: string) => void
  disabled?: boolean
  /** Caller's own exchange-scoped instrument list — not the globally-selected
   * account's (see OrderRow, which fetches this per-row via the row's own
   * exchange). Reading globalInstrumentsAtom here directly resolved options
   * for whichever account happened to be active, not the row's own exchange. */
  instruments: Instrument[]
}

export function QuoteAssetDropdown({
  base,
  quote,
  kind,
  onQuoteChange,
  disabled,
  instruments,
}: QuoteAssetDropdownProps) {
  const rawAvailableQuotes = getAvailableQuotes(instruments, base, kind)
  // Radix's <Select> renders its trigger blank when `value` doesn't match any
  // <SelectItem> — always include the actual current quote even if `instruments`
  // (e.g. still loading, or a transient mismatch) doesn't know it.
  const availableQuotes = rawAvailableQuotes.includes(quote)
    ? rawAvailableQuotes
    : [quote, ...rawAvailableQuotes]

  if (availableQuotes.length <= 1) {
    return <span className="font-ibm-plex-sans text-sm font-medium text-white">{quote}</span>
  }

  return (
    <Select value={quote} onValueChange={onQuoteChange} disabled={disabled}>
      <SelectTrigger
        hideIcon
        data-1p-ignore
        tabIndex={-1}
        className="font-ibm-plex-sans h-auto w-auto cursor-pointer border-none bg-transparent p-0 text-sm font-medium text-white hover:underline disabled:opacity-50"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="z-[60]">
        {availableQuotes.map((q) => (
          <SelectItem key={q} value={q} className="hover:bg-foreground/10 cursor-pointer text-sm">
            {q}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
